import { Controller } from '@hotwired/stimulus'

// The modal renders two kinds of entry (SS-2725). A `subscription` already
// exists and is activated in place; an `eligibility` has no subscription yet, so
// confirming it records payroll-deduction consent and starts the Hint membership
// the enrollment seam withheld. An entry with no `kind` is treated as a
// subscription, so a page served before this change keeps working.
function identifierFor(activation) {
  if (activation?.kind === 'eligibility') return { eligibility_id: activation.id }

  return { subscription_id: activation.id }
}

export default class extends Controller {
  static targets = [
    'companyName',
    'progress',
    'currentIndex',
    'totalCount',
    'activateButton',
    'skipButton',
    'payrollCheckbox',
  ]

  static values = {
    activations: Array,
    successUrl: { type: String, default: '/?activated=true' },
  }

  connect() {
    this.currentIndex = 0
    this.pendingActivations = this.activationsValue
    this.updateDisplay()
  }

  updateDisplay() {
    const current = this.pendingActivations[this.currentIndex]
    if (!current) {
      // All done - redirect to configured success URL
      window.location.href = this.successUrlValue
      return
    }

    // Update company name
    this.companyNameTarget.querySelector('span').textContent = current.organization_name || 'Your Employer'

    // Show progress if multiple
    if (this.pendingActivations.length > 1) {
      this.progressTarget.classList.remove('d-none')
      this.currentIndexTarget.textContent = this.currentIndex + 1
      this.totalCountTarget.textContent = this.pendingActivations.length
    }

    // Show/hide and reset payroll checkbox based on current subscription
    const requiresPayroll = current?.payroll_deduction
    if (this.hasPayrollCheckboxTarget) {
      const checkboxContainer = this.payrollCheckboxTarget
      const checkbox = checkboxContainer.querySelector('input[type="checkbox"]')

      if (requiresPayroll) {
        checkboxContainer.classList.remove('d-none')
      } else {
        checkboxContainer.classList.add('d-none')
      }

      if (checkbox) {
        checkbox.checked = false
      }
    }

    // Reset button state based on payroll checkbox requirement
    this.updateActivateButtonState()
  }

  toggleActivateButton() {
    this.updateActivateButtonState()
  }

  updateActivateButtonState() {
    const current = this.pendingActivations[this.currentIndex]
    const requiresPayroll = current?.payroll_deduction

    if (requiresPayroll && this.hasPayrollCheckboxTarget) {
      const checkbox = this.payrollCheckboxTarget.querySelector('input[type="checkbox"]')
      this.activateButtonTarget.disabled = !checkbox?.checked
    } else {
      this.activateButtonTarget.disabled = false
    }

    this.activateButtonTarget.textContent = 'Activate My Membership'
  }

  async activate(event) {
    event.preventDefault()
    const button = this.activateButtonTarget
    const current = this.pendingActivations[this.currentIndex]

    button.disabled = true
    button.textContent = 'Activating...'

    try {
      const response = await fetch('/api/membership_activations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        },
        body: JSON.stringify({
          ...identifierFor(current),
          payroll_terms_accepted: this.isPayrollAccepted(current),
        }),
        redirect: 'error',
      })

      if (response.ok) {
        // Move to next subscription
        this.currentIndex++
        this.updateDisplay()
      } else {
        alert('Failed to activate membership. Please try again.')
        this.updateActivateButtonState()
      }
    } catch (error) {
      alert('An error occurred. Please try again.')
      this.updateActivateButtonState()
    }
  }

  isPayrollAccepted(activation) {
    if (!activation?.payroll_deduction || !this.hasPayrollCheckboxTarget) return false

    const checkbox = this.payrollCheckboxTarget.querySelector('input[type="checkbox"]')
    return checkbox?.checked || false
  }
}
